import React, { useState, useEffect } from "react";
import { agentService } from "../../services/agentService";
import {
  XMarkIcon,
  MagnifyingGlassIcon,
  MapPinIcon,
  PhoneIcon,
  UserIcon,
} from "@heroicons/react/24/outline";
import LogoLoader from "../Common/LogoLoader";

const RedirectOrderModal = ({ isOpen, onClose, binId, onRedirectSuccess, currentAgentId }) => {
  const [agents, setAgents] = useState([]);
  const [filteredAgents, setFilteredAgents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [redirecting, setRedirecting] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedState, setSelectedState] = useState("all");
  const [message, setMessage] = useState("");
  const [selectedAgentId, setSelectedAgentId] = useState(null);

  useEffect(() => {
    if (isOpen) {
      fetchAgents();
    }
  }, [isOpen]);

  useEffect(() => {
    filterAgents();
  }, [agents, searchTerm, selectedState]);

  const fetchAgents = async () => {
    try {
      setLoading(true);
      const data = await agentService.getAllAgents();
      // Filter out the current agent to prevent redirecting to self
      const filtered = currentAgentId 
        ? data.filter(agent => agent._id !== currentAgentId)
        : data;
      setAgents(filtered);
      setFilteredAgents(filtered);
    } catch (error) {
      console.error("Error fetching agents:", error);
      setMessage("Error loading agents. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const filterAgents = () => {
    let filtered = agents;

    // Filter by search term
    if (searchTerm) {
      filtered = filtered.filter(
        (agent) =>
          agent.agent_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          agent.state?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          agent.localGovernmentAddress?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          agent.streetAddress?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          agent.phoneNumber?.includes(searchTerm) ||
          agent.phone_number?.includes(searchTerm)
      );
    }

    // Filter by state
    if (selectedState !== "all") {
      filtered = filtered.filter((agent) => agent.state === selectedState);
    }

    setFilteredAgents(filtered);
  };

  // Get unique states for filter
  const states = [
    ...new Set(agents.map((agent) => agent.state).filter(Boolean)),
  ].sort();

  const handleRedirect = async () => {
    if (!selectedAgentId) {
      setMessage("Please select an agent to redirect the order to");
      return;
    }

    setRedirecting(true);
    setMessage("");

    try {
      await agentService.redirectOrder(binId, selectedAgentId);
      setMessage("Order redirected successfully!");
      setTimeout(() => {
        onRedirectSuccess();
        onClose();
      }, 1500);
    } catch (error) {
      console.error("Error redirecting order:", error);
      setMessage(
        error.response?.data?.message || "Error redirecting order. Please try again."
      );
    } finally {
      setRedirecting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
        {/* Background overlay */}
        <div
          className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75"
          onClick={onClose}
        ></div>

        {/* Modal panel */}
        <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-4xl sm:w-full">
          {/* Header */}
          <div className="bg-green-600 px-6 py-4 flex items-center justify-between">
            <h3 className="text-lg font-semibold text-white">
              Redirect Order to Another Agent
            </h3>
            <button
              onClick={onClose}
              className="text-white hover:text-gray-200 transition-colors"
            >
              <XMarkIcon className="h-6 w-6" />
            </button>
          </div>

          {/* Content */}
          <div className="px-6 py-4">
            {/* Search and Filter */}
            <div className="mb-6 space-y-4">
              <div className="relative">
                <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search by name, location, or phone..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                />
              </div>

              <div className="flex items-center space-x-4">
                <label className="text-sm font-medium text-gray-700">
                  Filter by State:
                </label>
                <select
                  value={selectedState}
                  onChange={(e) => setSelectedState(e.target.value)}
                  className="border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-green-500 focus:border-green-500"
                >
                  <option value="all">All States</option>
                  {states.map((state) => (
                    <option key={state} value={state}>
                      {state}
                    </option>
                  ))}
                </select>
                <span className="text-sm text-gray-600">
                  {filteredAgents.length} agent(s) found
                </span>
              </div>
            </div>

            {/* Message */}
            {message && (
              <div
                className={`mb-4 p-3 rounded-lg ${
                  message.includes("successfully")
                    ? "bg-green-100 text-green-700"
                    : "bg-red-100 text-red-700"
                }`}
              >
                {message}
              </div>
            )}

            {/* Agents List */}
            {loading ? (
              <div className="text-center py-12">
                <LogoLoader />
                <p className="mt-2 text-gray-600">Loading agents...</p>
              </div>
            ) : filteredAgents.length === 0 ? (
              <div className="text-center py-12">
                <p className="text-gray-600">No agents found matching your criteria.</p>
              </div>
            ) : (
              <div className="max-h-96 overflow-y-auto space-y-3">
                {filteredAgents.map((agent) => {
                  const details = agent.agentDetails || {};
                  const agentName =
                    details.agent_name || agent.agent_name || agent.username;
                  const agentImage = details.agentImage || agent.agentImage;
                  const phoneNumber =
                    details.phone_number || agent.phone_number || agent.phoneNumber;
                  const state = agent.state || details.state;
                  const lga =
                    details.localGovernmentAddress || agent.localGovernmentAddress;
                  const streetAddress =
                    details.streetAddress || agent.streetAddress;

                  const isSelected = selectedAgentId === agent._id;

                  return (
                    <div
                      key={agent._id}
                      onClick={() => setSelectedAgentId(agent._id)}
                      className={`p-4 border-2 rounded-lg cursor-pointer transition-all ${
                        isSelected
                          ? "border-green-600 bg-green-50"
                          : "border-gray-200 hover:border-green-300 hover:bg-gray-50"
                      }`}
                    >
                      <div className="flex items-start space-x-4">
                        {/* Agent Image */}
                        <div className="flex-shrink-0">
                          {agentImage ? (
                            <img
                              src={agentImage}
                              alt={agentName}
                              className="h-16 w-16 rounded-full object-cover border-2 border-gray-200"
                            />
                          ) : (
                            <div className="h-16 w-16 rounded-full bg-gray-200 flex items-center justify-center">
                              <UserIcon className="h-8 w-8 text-gray-400" />
                            </div>
                          )}
                        </div>

                        {/* Agent Info */}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between">
                            <h4 className="text-lg font-semibold text-gray-900">
                              {agentName}
                            </h4>
                            {isSelected && (
                              <span className="px-2 py-1 text-xs font-medium bg-green-600 text-white rounded">
                                Selected
                              </span>
                            )}
                          </div>

                          <div className="mt-2 space-y-1">
                            {phoneNumber && (
                              <div className="flex items-center text-sm text-gray-600">
                                <PhoneIcon className="h-4 w-4 mr-2" />
                                {phoneNumber}
                              </div>
                            )}

                            <div className="flex items-start text-sm text-gray-600">
                              <MapPinIcon className="h-4 w-4 mr-2 mt-0.5 flex-shrink-0" />
                              <div>
                                {state && <p className="font-medium">{state}</p>}
                                {lga && <p>{lga}</p>}
                                {streetAddress && (
                                  <p className="text-xs text-gray-500">
                                    {streetAddress}
                                  </p>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="bg-gray-50 px-6 py-4 flex items-center justify-end space-x-3">
            <button
              onClick={onClose}
              disabled={redirecting}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              onClick={handleRedirect}
              disabled={redirecting || !selectedAgentId}
              className="px-4 py-2 text-sm font-medium text-white bg-green-600 rounded-lg hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {redirecting ? "Redirecting..." : "Redirect Order"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RedirectOrderModal;
